import { EngagementFlowWithProgramEngagementFlowDto } from "../../services/api";

interface EngagementDisplayDefaults {
  title?: string;
  description?: string;
  longDescription?: string;
  iconUrl?: string;
}

/**
 * Defaults shown in the display-config form when a program has not yet
 * configured a flow (i.e. `programEngagementFlows` is empty and no
 * default-locale translation exists yet). Mirror the backend's hardcoded
 * fallbacks so merchants can hit "Save & activate" without rewriting the copy
 * or re-uploading the icon from scratch. Persisted values still win over these
 * defaults so manual edits are never silently replaced.
 *
 * Entries may be partial: flows without backend-provided text defaults still
 * get a default `iconUrl` so the icon slot is never left empty on first load.
 *
 * Copy is customer-facing: these titles and descriptions render on the earning
 * flow card the end customer sees in the webapp/wallet, so write them in
 * second person addressed to the customer, not as a merchant-facing pitch.
 */
export const ENGAGEMENT_DISPLAY_CONFIG_DEFAULTS: Partial<
  Record<EngagementFlowWithProgramEngagementFlowDto.NameEnum, EngagementDisplayDefaults>
> = {
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.CompleteUserProfile]: {
    title: "Complete your profile",
    description: "Fill in your profile and earn a reward. It only takes a minute.",
    longDescription: "Add a few details like your name, phone, or birthday and earn a reward for it. It also helps us send you offers and perks that actually match what you like.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/profile.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.CustomerScanned]: {
    title: "Check in at our stores",
    description: "Scan your card when you visit and earn points. No purchase needed.",
    longDescription: "Every time you visit one of our stores, scan or tap your loyalty card to check in and earn points. No purchase required, just stop by and say hi.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/scan.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.FollowUs]: {
    title: "Follow us",
    description: "Follow us on social media and earn points.",
    longDescription: "Follow us on social media to earn points and stay in the loop on new drops, promos, and perks. It only takes a tap.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/social.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.IncentivizePurchases]: {
    title: "Earn points on every purchase",
    description: "Get points every time you shop with us, online or in store.",
    longDescription: "Every order earns you points, whether you shop online or in store. The more you shop, the faster you reach your next reward.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/purchase.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.InstallWalletPass]: {
    title: "Add your loyalty card",
    description: "Install your loyalty card and earn points right away.",
    longDescription: "Add your loyalty card to your phone so you earn points straight away and keep your rewards one tap from your home screen.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/wallet.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.MemberGetMember]: {
    title: "Invite a friend",
    description: "Refer a friend and you both earn a reward.",
    longDescription: "Share your referral link with friends. When a friend joins and qualifies, you both get rewarded. Invite as many as you like and keep earning.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/referral.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.VerifyAccount]: {
    title: "Welcome customer",
    description: "Welcome new customers with points when they confirm their account, building engagement and trust from day one.",
    longDescription: "Welcome new customers and reward them for confirming their account. A warm welcome boosts engagement, and confirmed contact details improve deliverability and unlock more reliable marketing channels.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/verify.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.WalletLinkClick]: {
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/link.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom1]: {
    title: "Earn extra points",
    description: "Complete a special action and earn points.",
    longDescription: "Earn points by completing a special action set up for this program. Keep an eye out for new ways to get rewarded.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/custom-1.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom2]: {
    title: "Earn extra points",
    description: "Complete a special action and earn points.",
    longDescription: "Earn points by completing a special action set up for this program. Keep an eye out for new ways to get rewarded.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/custom-2.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom3]: {
    title: "Earn extra points",
    description: "Complete a special action and earn points.",
    longDescription: "Earn points by completing a special action set up for this program. Keep an eye out for new ways to get rewarded.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/custom-3.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom4]: {
    title: "Earn extra points",
    description: "Complete a special action and earn points.",
    longDescription: "Earn points by completing a special action set up for this program. Keep an eye out for new ways to get rewarded.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/custom-4.png",
  },
  [EngagementFlowWithProgramEngagementFlowDto.NameEnum.Custom5]: {
    title: "Earn extra points",
    description: "Complete a special action and earn points.",
    longDescription: "Earn points by completing a special action set up for this program. Keep an eye out for new ways to get rewarded.",
    iconUrl: "https://brand-assets.jericommerce.com/earning-flows/custom-5.png",
  },
};
